import _ from 'lodash';
import {RegistrationSchema} from '../../../../campaign/structure/event-page';
import {FormsModule, ReactiveFormsModule, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup} from '@angular/forms';
import { select, Store } from '@ngrx/store';
import {Component, DestroyRef, inject, OnDestroy, OnInit} from '@angular/core';
import {CampaignService} from 'app/campaign/services/campaign.service';
import {selectDialogState} from 'app/dialog/features/dialog.selectors';
import {DialogService} from 'app/dialog/services/dialog.service';
import { first, map } from 'rxjs/operators';
import {Observable} from 'rxjs';
import {
  selectTicketingCampaignList
} from '../../../../campaign/features/campaign.selectors';
import {
  addRegistration,
  updateRegistration
} from '../../../../event/features/event.actions';
import {selectEventConfig} from '../../../../event/features/event.selectors';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { MdbFormsModule } from 'mdb-angular-ui-kit/forms';
import { MdbSelectModule } from 'mdb-angular-ui-kit/select';
import { MdbpInputComponent } from '../../mdb-input/mdb-input.component';
import { MdbTextareaComponent } from '../../mdb-textarea/mdb-textarea.component';

export interface RegistrationTypeModel {
  id: number;
  label: string;
}

@Component({
  standalone:true,
  imports:[CommonModule,FormsModule,ReactiveFormsModule,MdbFormsModule,MdbSelectModule,MdbpInputComponent,MdbTextareaComponent],
  selector: 'app-registation-button-dialog',
  templateUrl: './registration-button-dialog.component.html',
  styleUrls: ['./registration-button-dialog.component.scss']
})
export class RegistrationButtonDialogComponent implements OnInit, OnDestroy {
  destroyRef = inject(DestroyRef)
  public dialog$: Observable<any> = this.store.pipe(select(selectDialogState));

  public selectedCampaign: string = '';
  public registrationType: number = 0;

  public eventConfigData: any = null;
  public button: UntypedFormGroup = null;
  public labelControl: UntypedFormControl = null;

  public registrationTypes: RegistrationTypeModel[] = [
    {id: 0, label: 'Ticketing'},
    {id: 1, label: 'RSVP'},
    {id: 2, label: 'Join'}
  ];

  public ticketingCampaigns$: any = this.store.pipe(
    select(selectTicketingCampaignList),
    map(list => {
      return _.filter(list, {status: 0});
    })
  );

  public loadEventConfig$ = this.store.pipe(
    select(selectEventConfig)
  );

  constructor(
    private store: Store,
    private formBuilder: UntypedFormBuilder,
    private campaignService: CampaignService,
    private dialogService: DialogService,
  ) {
    this.button = this.formBuilder.group({});
    this.campaignService.generateForm(this.button, RegistrationSchema);

    this.dialog$.pipe(
      first(),
      takeUntilDestroyed()
    ).subscribe(({initialState}) => {
      const option = _.get(initialState, 'option', null);
      if (option) {
        this.selectedCampaign = option.campaignId;
        this.registrationType = option.registrationType;

        this.campaignService.initializeForm(this.button, option, RegistrationSchema);
      }
    });
  }

  ngOnInit(): void {
    this.labelControl = this.button.get('label') as UntypedFormControl;

    this.loadEventConfig$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((eventConfig) => {
        this.eventConfigData = eventConfig;
      });

    this.ticketingCampaigns$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((campaigns) => {
        if (!this.selectedCampaign) {
          this.selectedCampaign = campaigns.length && campaigns[0].id;
        }
      });
  }

  ngOnDestroy(): void {
    this.dialogService.closeDialogSubject.next(null);
  }

  close(dialog, value) {
    const {initialState: {id, action}} = dialog;
    this.dialogService.closeDialogSubject.next({id, action, name: 'registrationButtonForm', value});
  }

  confirm(dialog, value) {
    if (this.button.invalid) {
      return this.button.markAllAsTouched();
    }

    if (this.eventConfigData ) {
      const eventInfo = this.getEventInfo(this.eventConfigData);


      if (dialog.initialState.action === 'add') {
        this.store.dispatch({
          type: addRegistration.type,
          payload: {
            eventPageId: eventInfo.eventPageId,
            eventPageRegistration: {
              registrationType: value.registrationType,
              virtualEventId: eventInfo.virtualEvent ? eventInfo.virtualEvent.id : eventInfo.id,
              campaignId: value.campaignId,
              label: value.label,
              desc: value.desc
            }
          }
        });
      }

      if (dialog.initialState.action === 'edit') {
        let eventPageRegistration;

        if (value.registrationType === 0) {
          eventPageRegistration = {
            id: value.id,
            registrationType: value.registrationType,
            campaignId: value.campaignId,
            label: value.label,
            desc: value.desc
          };
        } else {
          eventPageRegistration = {
            id: value.id,
            registrationType: value.registrationType,
            virtualEventId: eventInfo.virtualEvent ? eventInfo.virtualEvent.id : eventInfo.id,
            label: value.label,
            desc: value.desc
          };
        }

        this.store.dispatch({
          type: updateRegistration.type,
          payload: {
            eventPageId: eventInfo.eventPageId,
            eventPageRegistration
          }
        });
      }
    }

    return this.close(dialog, value);
  }

  private getEventInfo(eventObj) {
    let eventInfo;

    if (eventObj.virtualEventInfo) {
      eventInfo = eventObj.virtualEventInfo;
    }

    if (eventObj.inPersonEventInfo) {
      eventInfo = eventObj.inPersonEventInfo;
    }

    if (eventObj.hybridEventInfo) {
      eventInfo = eventObj.hybridEventInfo;
    }

    return eventInfo;
  }
}
